import styled, { keyframes } from 'styled-components'

/**
 * The one dark surface on the page. Everything about asking happens inside it,
 * so a visitor can tell at a glance which part is the product and which part is
 * the page talking about the product.
 *
 * It runs on a single accent — Loadsmart's brand green — for anything the
 * assistant does. No second accent colour: the green is what the eye should
 * follow from the selected question down to the call to action.
 */
const PANEL_BACKGROUND = 'rgb(var(--m-color-primary-100))'
const PANEL_HAIRLINE = 'rgba(var(--m-color-neutral-0), 0.14)'
const PANEL_TEXT = 'rgb(var(--m-color-text-inverted))'
const PANEL_TEXT_MUTED = 'rgba(var(--m-color-neutral-0), 0.58)'

export const StyledPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background-color: ${PANEL_BACKGROUND};
  border-radius: var(--m-border-radius-l);
`

export const StyledPanelBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-3);
  padding: var(--m-spacing-3) var(--m-spacing-5);
  border-bottom: var(--m-border-thin) solid ${PANEL_HAIRLINE};
`

export const StyledPanelTitle = styled.span`
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  letter-spacing: 0.02em;
  color: ${PANEL_TEXT};
`

/**
 * States outright that the numbers below are invented. It sits in the panel's
 * title bar rather than in a footnote because it qualifies everything inside.
 */
export const StyledSampleBadge = styled.span`
  padding: 2px var(--m-spacing-2);
  border-radius: 999px;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-1);
  font-weight: var(--m-font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${PANEL_TEXT_MUTED};
  border: var(--m-border-thin) solid ${PANEL_HAIRLINE};
`

export const StyledPanelSpacer = styled.span`
  flex: 1;
`

export const StyledReplayButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: var(--m-spacing-1) var(--m-spacing-3);
  cursor: pointer;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  color: ${PANEL_TEXT};
  background-color: transparent;
  border: var(--m-border-thin) solid rgba(var(--m-color-neutral-0), 0.28);
  border-radius: 999px;

  &:hover:not(:disabled) {
    background-color: rgba(var(--m-color-neutral-0), 0.12);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }

  /* Disabled while a sample is still playing — replaying mid-run would restart
     it from a step the reader has not seen yet. */
  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`

/* --- the questions -------------------------------------------------------- */

export const StyledQuestionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-3);
  padding: var(--m-spacing-4) var(--m-spacing-5);
  border-bottom: var(--m-border-thin) solid ${PANEL_HAIRLINE};
`

/**
 * One row per product. The row label is the structure: it says which part of
 * your operation the questions beside it reach into, which is information the
 * questions themselves do not carry.
 */
export const StyledQuestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--m-spacing-2);
`

export const StyledQuestionRowLabel = styled.span`
  flex: 0 0 96px;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-1);
  font-weight: var(--m-font-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${PANEL_TEXT_MUTED};
`

export const StyledQuestionChip = styled.button<{ $active: boolean }>`
  padding: var(--m-spacing-2) var(--m-spacing-4);
  cursor: pointer;
  text-align: left;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-4);
  border-radius: 999px;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  color: ${({ $active }) => ($active ? 'rgb(var(--m-color-primary-100))' : PANEL_TEXT)};
  background-color: ${({ $active }) => ($active ? 'rgb(var(--m-color-brand))' : 'transparent')};
  border: var(--m-border-thin) solid
    ${({ $active }) =>
      $active ? 'rgb(var(--m-color-brand))' : 'rgba(var(--m-color-neutral-0), 0.24)'};
  font-weight: ${({ $active }) =>
    $active ? 'var(--m-font-weight-medium)' : 'var(--m-font-weight-regular)'};

  &:hover {
    border-color: ${({ $active }) => ($active ? 'rgb(var(--m-color-brand))' : PANEL_TEXT)};
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`

/* --- the exchange --------------------------------------------------------- */

export const StyledExchange = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-4);
  padding: var(--m-spacing-6) var(--m-spacing-5);
  /* Holds the panel's height steady while a sample reveals itself, so the page
     does not shuffle under the reader between steps. */
  min-height: 520px;

  @media screen and (min-width: 900px) {
    padding: var(--m-spacing-8);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`

export const StyledStep = styled.div`
  animation: ${fadeIn} 0.28s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const StyledQuestion = styled.p`
  margin: 0;
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-6);
  line-height: var(--m-line-height-5);
  color: ${PANEL_TEXT};
`

export const StyledStatusLine = styled.p<{ $tone: 'muted' | 'done' }>`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2);
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  color: ${({ $tone }) => ($tone === 'done' ? 'rgb(var(--m-color-brand))' : PANEL_TEXT_MUTED)};
`

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`

export const StyledWorkingDots = styled.span`
  display: inline-flex;
  gap: 3px;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
    animation: ${pulse} 1.2s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
      opacity: 0.6;
    }
  }
`

export const StyledAnswer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-5);
`

/**
 * The answer in words, set larger than the table under it. That inversion is
 * deliberate: a dashboard already gives you the grid, and what this product
 * adds is the sentence. The rule on the left marks it as the assistant's
 * reading rather than a heading of your own.
 */
export const StyledSummary = styled.p`
  margin: 0;
  padding-left: var(--m-spacing-4);
  border-left: 2px solid rgb(var(--m-color-brand));
  font-family: var(--m-font-family-title);
  font-size: var(--m-font-size-5);
  line-height: 150%;
  color: ${PANEL_TEXT};
`

export const StyledTableWrapper = styled.div`
  /* Wide result tables scroll inside the panel instead of stretching it. */
  overflow-x: auto;
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  color: ${PANEL_TEXT};

  th,
  td {
    padding: var(--m-spacing-3);
    text-align: left;
    white-space: nowrap;
    border-bottom: var(--m-border-thin) solid ${PANEL_HAIRLINE};
  }

  th {
    padding-top: 0;
    font-size: var(--m-font-size-1);
    font-weight: var(--m-font-weight-bold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${PANEL_TEXT_MUTED};
  }

  /* Measures line up on the right and share a digit width, so a column of them
     can be compared by shape without reading every value. */
  th.measure,
  td.measure {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* The name column absorbs the slack so the measures stay packed together on
     the right, close enough to compare without the eye travelling. */
  th:first-child,
  td:first-child {
    width: 100%;
    white-space: normal;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`

export const StyledFollowUp = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--m-spacing-4);
  padding-top: var(--m-spacing-2);
`

export const StyledFollowUpText = styled.p`
  flex: 1 1 320px;
  margin: 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-5);
  color: ${PANEL_TEXT_MUTED};
`

/**
 * The panel's only call to action, styled here because Miranda's buttons are
 * drawn for light surfaces and lose their edges on this one.
 */
export const StyledFollowUpAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--m-spacing-2);
  flex: 0 0 auto;
  padding: var(--m-spacing-3) var(--m-spacing-5);
  cursor: pointer;
  white-space: nowrap;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-primary-100));
  background-color: rgb(var(--m-color-brand));
  border: var(--m-border-thin) solid rgb(var(--m-color-brand));
  border-radius: 999px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgb(var(--m-color-brand-accent-2));
    border-color: rgb(var(--m-color-brand-accent-2));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`
